import React, { useEffect, useState } from "react";
import { authActions, logout } from "../../redux/actions/auth/authActions";
import { connect, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useNavigate } from "react-router-dom";
import { validaApiloginActions } from "../../redux/actions/auth/validaApiloginActions";
import { Helmet } from "react-helmet-async";
import { Button } from "react-bootstrap";
import Swal from "sweetalert2";
import { AnimatePresence, motion } from "framer-motion";
import { setOrigenLoginActions } from "../../redux/actions/auth/setOrigenLoginActions";
import { desencriptarURLActions } from "../../redux/actions/auth/desencriptarURLActions";

interface Props {
  authActions: (usuario: string, password: string) => Promise<boolean>;
  validaApiloginActions: (rut: string) => Promise<number>;
  desencriptarURLActions: (datosPersona: string | null, tokenAplicacion: string | null) => Promise<string | null>;
  logout: () => void;
  isDarkMode: boolean;
}

const ValidaPortal: React.FC<Props> = ({ authActions, logout, validaApiloginActions, desencriptarURLActions, isDarkMode }) => {
  const navigate = useNavigate();
  const [showButton, setShowButton] = useState(false);
  const usuario = import.meta.env.VITE_USUARIO_API_LOGIN;
  const password = import.meta.env.VITE_PASSWORD_API_LOGIN;
  const dispatch = useDispatch<AppDispatch>();
  const [currentMessage, setCurrentMessage] = useState("Iniciando proceso de autenticación...");
  useEffect(() => {
    // Temporizador para mostrar el botón después de 20 segundos
    const timer = setTimeout(() => {
      setShowButton(true);
    }, 20000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    capturarDatosPersona();
  }, []);

  useEffect(() => {
    // Limpieza "forzada" por si queda algo colgando en el almacenamiento
    localStorage.removeItem("token");
    sessionStorage.clear();
    // dispatch(logout()); // Esto depende de lo que haga internamente tu logout

    // continuar flujo normal...
  }, [])

  const esperar = (milisegundos: number) =>
    new Promise(resolve => setTimeout(resolve, milisegundos));

  const capturarDatosPersona = async () => {
    try {

      setCurrentMessage("Validando credenciales...");
      await esperar(500);

      const params = new URLSearchParams(window.location.search);
      const datosPersona = params.get("datosPersona");
      const token_aplicacion = params.get("token_aplicacion");

      if (datosPersona) {

        let rutUsuario = null;

        // Desencriptar o recuperar los datos del usuario
        if (token_aplicacion) {

          // setCurrentMessage("Desencriptando información del usuario...");
          // await esperar(2000);

          rutUsuario = await desencriptarURLActions(
            datosPersona,
            token_aplicacion
          );

        } else {

          // setCurrentMessage("Procesando información de autenticación...");
          // await esperar(2000);

          const datos = JSON.parse(decodeURIComponent(datosPersona));
          rutUsuario = datos.sub;
        }

        // Obtener token de autenticación
        // setCurrentMessage("Validando credenciales de conexión...");
        // await esperar(2000);

        const obtieneToken = await authActions(usuario, password);

        if (obtieneToken) {

          // Validar acceso a la aplicación
          // setCurrentMessage("Verificando permisos de acceso al inventario...");
          // await esperar(2000);

          const esValido = await validaApiloginActions(rutUsuario);

          if (esValido === 1) {

            setCurrentMessage("Accediendo al sistema de inventario...");
            await esperar(1000);

            navigate("/Inicio");

          } else if (esValido === -1) {

            setCurrentMessage("Acceso denegado. Verificando permisos de usuario...");
            await esperar(1000);

            dispatch(logout());
            navigate("/Denegado");
          }

        } else {

          localStorage.removeItem("token");
          sessionStorage.clear();
          dispatch(logout());

          navigate("/");

          Swal.fire({
            icon: "warning",
            title: "Error de Conexión",
            text: "Se ha perdido la comunicación con el servidor. Le recomendamos intentar más tarde. Si el problema continúa, por favor, comuníquese con el equipo de Mesa de Ayuda o Desarrollo.",
            background: `${isDarkMode ? "#1e1e1e" : "#ffffff"}`,
            color: `${isDarkMode ? "#ffffff" : "#000000"}`,
            confirmButtonColor: `${isDarkMode ? "#6c757d" : "#444"}`,
            customClass: {
              popup: "custom-border",
            }
          });
        }
      }

      if (datosPersona?.length === 18) {
        dispatch(setOrigenLoginActions(0));
      } else {
        dispatch(setOrigenLoginActions(1));
      }

    } catch (error) {
      console.error("Error durante la validación del portal:", error);

      setCurrentMessage("Ocurrió un error durante la autenticación. Intente nuevamente.");
    }
  };

  useEffect(() => {
    capturarDatosPersona();
  }, []);

  return (
    <>
      <Helmet>
        <title>Validando credenciales...</title>
      </Helmet>
      <div className={`d-flex justify-content-center align-items-center vh-100 ${isDarkMode ? "bg-color-dark" : ""}`}>
        <div className="col-12 col-md-8 text-center">
          <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: "200px" }}>
            <div className="spinner-border text-primary mb-3" role="status" />

            <AnimatePresence mode="wait">
              <motion.p
                key={currentMessage}
                className={`${isDarkMode ? "text-white" : "text-muted"} mb-1`}
                initial={{ opacity: 0, y: -15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 15 }}
                transition={{ duration: 0.4 }}
              >
                {currentMessage}
              </motion.p>
            </AnimatePresence>
          </div>
          {showButton && (
            <div className="m-4 rounded d-inline-block">
              <p className={`${isDarkMode ? "text-white" : "text-muted"} mb-2`}>
                El proceso está tardando más de lo esperado. Si lo prefiere, puede volver a intentarlo.
              </p>
              <Button variant={`${isDarkMode ? "secondary" : "primary"}`} onClick={() => window.location.reload()}>
                Reintentar
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  isDarkMode: state.darkModeReducer.isDarkMode
});

export default connect(mapStateToProps, {
  authActions,
  logout,
  validaApiloginActions,
  desencriptarURLActions
})(ValidaPortal);
